<script setup>
    import { ref, computed, watch } from 'vue'

    const emit = defineEmits(['close'])
    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
    })

    const selectedPackage = ref('player')
    const isProcessing = ref(false)
    const promoCode = ref('')
    const showPromoInput = ref(false)

    // Reset to 'player' package (19.99) when modal opens
    watch(
        () => props.show,
        isOpen => {
            if (isOpen) {
                selectedPackage.value = 'player'
            }
        },
    )

    const packages = [
        {
            id: 'teaser',
            name: '500',
            bonusTokens: 0,
            totalTokens: 500,
            price: 4.99,
            pricePerToken: '$1.00 per 100',
            badge: null,
        },
        {
            id: 'player',
            name: '2,200',
            bonusTokens: 200,
            totalTokens: 2200,
            price: 19.99,
            pricePerToken: '$0.91 per 100',
            badge: 'Popular',
        },
        {
            id: 'allstar',
            name: '5,600',
            bonusTokens: 600,
            totalTokens: 6400,
            price: 49.99,
            pricePerToken: '$0.89 per 100',
            badge: null,
        },
    ]

    const selectedPackageDetails = computed(() => {
        return packages.find(pkg => pkg.id === selectedPackage.value)
    })

    const handlePurchase = () => {
        isProcessing.value = true

        setTimeout(() => {
            isProcessing.value = false
            emit('close')
        }, 2000)
    }
</script>

<template>
    <div
        v-if="props.show"
        class="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto p-4">
        <div class="absolute inset-0 bg-black/90 backdrop-blur-sm" @click="emit('close')"></div>

        <div
            class="animate-in fade-in zoom-in relative max-h-[90vh] w-full max-w-[380px] overflow-hidden overflow-y-auto rounded-3xl border border-white/10 bg-[#0b0b0b] shadow-2xl sm:max-w-md">
            <button
                type="button"
                aria-label="Close"
                class="absolute right-4 top-4 z-10 rounded-full p-1.5 transition-colors hover:bg-white/5"
                @click="emit('close')">
                <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <div class="px-6 pb-6 pt-8 text-center">
                <h2 class="mb-1 text-2xl font-bold text-white">Get FYF Credits</h2>
                <p class="text-sm text-neutral-500">Choose your package</p>
            </div>

            <div class="px-6 pb-6">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <button
                        v-for="pkg in packages"
                        :key="pkg.id"
                        type="button"
                        class="relative rounded-2xl border border-[#1a1a1a] bg-[#0f0f0f] p-4 text-left transition-all hover:border-[#2a2a2a]"
                        :class="
                            selectedPackage === pkg.id
                                ? 'border-2 border-[#65e8a4] bg-gradient-to-b from-[#65e8a4]/20 to-[#4bc990]/10 shadow-lg'
                                : ''
                        "
                        @click="selectedPackage = pkg.id">
                        <div
                            v-if="pkg.badge"
                            class="absolute -top-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[#ff1493] px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                            {{ pkg.badge }}
                        </div>

                        <div class="mb-3 flex justify-center">
                            <img
                                src="https://aws-lg.naughtycdn.com/mediaAssets/65028/FC_token.png"
                                alt="Fantasy Credits"
                                class="h-12 w-12 object-contain"
                                :class="
                                    selectedPackage === pkg.id
                                        ? 'drop-shadow-[0_0_8px_rgba(101,232,164,0.6)]'
                                        : 'opacity-70'
                                " />
                        </div>

                        <div class="mb-2 text-center">
                            <div class="text-xl font-bold text-white">{{ pkg.name }}</div>
                            <div
                                v-if="pkg.bonusTokens"
                                class="mt-1 text-[10px] font-bold uppercase tracking-wide text-[#65e8a4]">
                                +{{ pkg.bonusTokens }} Bonus
                            </div>
                        </div>

                        <div class="text-center">
                            <div class="mb-1 text-[11px] text-neutral-400">
                                {{ pkg.pricePerToken }}
                            </div>
                            <div class="text-base font-bold text-white">${{ pkg.price }}</div>
                        </div>

                        <div v-if="selectedPackage === pkg.id" class="absolute right-2 top-2">
                            <span
                                class="flex h-5 w-5 items-center justify-center rounded-full bg-[#65e8a4]">
                                <svg
                                    class="h-3 w-3 text-black"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="3"
                                        d="M5 13l4 4L19 7" />
                                </svg>
                            </span>
                        </div>
                    </button>
                </div>
            </div>

            <div class="px-6 pb-6">
                <button
                    type="button"
                    class="w-full rounded-full bg-white py-3.5 text-sm font-semibold text-black shadow-lg transition-all hover:scale-[1.02] hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-[#1a1a1a] disabled:text-gray-600"
                    :disabled="isProcessing"
                    @click="handlePurchase">
                    <span v-if="isProcessing" class="flex items-center justify-center gap-2">
                        <span
                            class="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"></span>
                        Processing...
                    </span>
                    <span v-else>Buy FYF Credits</span>
                </button>

                <p class="mt-3 text-center text-[11px] leading-relaxed text-gray-600">
                    By pressing "Buy now", you accept our Terms and authorize this purchase
                </p>
            </div>

            <div class="px-6 pb-6">
                <div
                    class="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] p-4 text-sm text-neutral-400">
                    <h4 class="mb-3 text-center text-sm font-semibold text-white">
                        Enhance your experience with FYF credits
                    </h4>
                    <ul class="space-y-2">
                        <li class="flex items-center gap-2">
                            <span class="h-1 w-1 rounded-full bg-[#65e8a4]"></span>
                            <span>DM creators directly</span>
                        </li>
                        <li class="flex items-center gap-2">
                            <span class="h-1 w-1 rounded-full bg-[#65e8a4]"></span>
                            <span>Request custom personalized content</span>
                        </li>
                        <li class="flex items-center gap-2">
                            <span class="h-1 w-1 rounded-full bg-[#65e8a4]"></span>
                            <span>Unlock exclusive photos & videos</span>
                        </li>
                        <li class="flex items-center gap-2">
                            <span class="h-1 w-1 rounded-full bg-[#65e8a4]"></span>
                            <span>Show your appreciation</span>
                        </li>
                        <li class="flex items-center gap-2">
                            <span class="h-1 w-1 rounded-full bg-[#65e8a4]"></span>
                            <span>Access VIP features</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="px-6 pb-6 text-center">
                <button
                    type="button"
                    class="text-sm text-neutral-400 underline transition-colors hover:text-neutral-300">
                    Manage my payment info
                </button>
            </div>
        </div>
    </div>
</template>
